import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface BasicModalProps {
  /** Accessible label announced by screen readers when the dialog opens. */
  'aria-label': string;
  children: React.ReactNode;
  /** Called when the dialog is dismissed via the Escape key.
   * You should wire your onClose callback into this as well as any close buttons
   * so that state stays in sync when the user hits escape to close
   */
  onCancel: () => void;
}

/**
 * Accessible modal using a native <dialog> element with .showModal().
 * Mount to open, unmount to close — cleanup is handled automatically.
 *
 * Do no use this component in application code (talent, reps, casting, etc), it should always be built into a minecraft or bloc
 * component, this is just a primitive Modal component that does not give any content at all
 */
export const BasicModal: React.FC<BasicModalProps> = ({ 'aria-label': ariaLabel, children, onCancel }) => {
  const [portalContainer, setPortalContainer] = useState<HTMLDialogElement | null>(null);

  // Excludes `onCancel` from deps so a new callback identity each render doesn't remount the dialog.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return () => {};
    }

    const dialog = document.createElement('dialog');
    dialog.setAttribute('data-testid', 'basic-modal-portal-root');
    dialog.className = 'cn_modal-portal-root';
    dialog.setAttribute('aria-label', ariaLabel);
    document.body.appendChild(dialog);
    setPortalContainer(dialog);

    // .showModal() traps focus inside and makes background inert
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // Fallback for environments without .showModal() support (e.g. jsdom)
      dialog.setAttribute('open', '');
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      document.body.removeChild(dialog);
      document.body.style.overflow = originalOverflow;
      setPortalContainer(null);
    };
  }, [ariaLabel]);

  // Kept separate from the mount effect so an `onCancel` identity change only
  // swaps the listener instead of tearing down and recreating the dialog.
  useEffect(() => {
    if (!portalContainer) {
      return () => {};
    }

    // cancel fires on the dialog element itself when Escape is pressed — it does not bubble
    const handleCancel = () => onCancel?.();
    portalContainer.addEventListener('cancel', handleCancel);

    return () => {
      portalContainer.removeEventListener('cancel', handleCancel);
    };
  }, [portalContainer, onCancel]);

  if (!portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer);
};
